/**
 * Outstanding email-OTP tracking for the login modal.
 *
 * The modal used to hold "we already mailed a code to X" purely in component
 * state, so closing it — by the X, Esc, a backdrop click, or the route-change
 * watcher — threw that away. Coming back meant re-submitting the address, which
 * mailed a SECOND code and, often enough, came back
 * `429 over_email_send_rate_limit` instead: the user was locked out of an
 * account whose valid code was already sitting in their inbox.
 *
 * Persisting the record instead lets the modal reopen straight onto the code
 * step, and lets a re-submit of the same address skip the send entirely.
 *
 * Stored in localStorage rather than sessionStorage on purpose: the user leaves
 * to fetch the code from a webmail tab, and on a phone that can mean the tab is
 * evicted and restored, which sessionStorage would not survive.
 */

/**
 * Shape written to storage. `sentAt` is a client `Date.now()` from when the send
 * resolved.
 *
 * `active` separates two facts that look like one: "a code was mailed to this
 * address and is still valid" (governs whether a re-submit needs to send at all)
 * from "the user is mid-code-entry" (governs whether reopening the modal should
 * land back on the code step). Going back revokes only the second — the mailed
 * code is still sitting in their inbox either way, and re-sending to it is the
 * request that gets rate-limited.
 */
export interface OtpSession {
  email: string
  sentAt: number
  active: boolean
}

const STORAGE_KEY = 'aurumrun_auth_otp_pending'

/**
 * How long a stored record may resurrect the code step.
 *
 * Deliberately far below GoTrue's own OTP lifetime (`[auth.email] otp_expiry`,
 * currently 3600s) so the restored screen can never outlive the code it is
 * asking for — a form that accepts a code the server has already forgotten is
 * worse than no restore at all.
 */
export const OTP_SESSION_TTL_MS = 10 * 60 * 1000

/**
 * Resend cooldown surfaced by the modal's countdown.
 *
 * Must stay >= `[auth.email] max_frequency` in apps/supabase/supabase/config.toml,
 * and is currently held EQUAL to it (60s): the countdown reaching zero is a
 * promise that the next send will go through, and GoTrue is the one that has to
 * keep it. Equal works because this clock starts when the send RESPONSE lands,
 * one round trip after the server stamped its own `sent_at` — so it runs out
 * slightly late rather than early. Change one of the two and change the other.
 */
export const OTP_RESEND_COOLDOWN_MS = 60 * 1000

/** Same normalization Supabase Auth applies, so a re-submit compares equal. */
export function normalizeOtpEmail(email: string): string {
  return email.trim().toLowerCase()
}

// localStorage is read through try/catch, not a `typeof window` guard: the prod
// SSR runtime exposes a truthy but partial `window` whose `.localStorage` access
// throws. Same reasoning as `safeRead` in layers/base/app/stores/ui.ts.
function safeStorage<T>(fn: (storage: Storage) => T, fallback: T): T {
  try {
    return fn(window.localStorage)
  } catch {
    return fallback
  }
}

export function useOtpSession() {
  /** Drop the record entirely — the code was used, or another login superseded it. */
  function clear(): void {
    safeStorage((storage) => storage.removeItem(STORAGE_KEY), undefined)
  }

  /**
   * The outstanding code request, or null when there is none, it aged past the
   * TTL, or storage holds something we didn't write. Expired and malformed
   * records are removed on read so they can't be re-examined every open.
   *
   * Returns dismissed records too — `active` is the caller's to check. A code
   * the user navigated away from is still a code we must not duplicate.
   */
  function read(): OtpSession | null {
    const raw = safeStorage((storage) => storage.getItem(STORAGE_KEY), null)
    if (!raw) return null

    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      clear()
      return null
    }

    const record = parsed as Partial<OtpSession> | null
    if (
      !record
      || typeof record.email !== 'string'
      || record.email.length === 0
      || typeof record.sentAt !== 'number'
      || !Number.isFinite(record.sentAt)
    ) {
      clear()
      return null
    }

    // `> Date.now()` catches a record written before a clock correction moved
    // the system time backwards; without it such a record would look fresh for
    // as long as the skew lasts.
    const age = Date.now() - record.sentAt
    if (age < 0 || age > OTP_SESSION_TTL_MS) {
      clear()
      return null
    }

    // Anything that isn't an explicit `false` counts as active, so a record
    // written by a build that predates this field still restores. Read as a
    // boolean rather than `!== false` so a hand-edited `"false"` string can't
    // slip through as truthy.
    const active = typeof record.active === 'boolean' ? record.active : true
    return { email: record.email, sentAt: record.sentAt, active }
  }

  function write(session: OtpSession): OtpSession {
    safeStorage(
      (storage) => storage.setItem(STORAGE_KEY, JSON.stringify(session)),
      undefined,
    )
    return session
  }

  /** Record a code we just mailed. Returns the stored record so callers can seed the countdown. */
  function save(email: string): OtpSession {
    return write({ email: normalizeOtpEmail(email), sentAt: Date.now(), active: true })
  }

  /**
   * The user left the code step ("wrong email"). Stop restoring it, but keep
   * remembering the code so retyping the same address doesn't mail a duplicate.
   * They still have Resend on the next screen if the first mail never arrived.
   */
  function dismiss(): void {
    const session = read()
    if (session) write({ ...session, active: false })
  }

  /**
   * The user came back to a dismissed address and is on the code step again, so
   * a reopen should land there once more.
   *
   * Deliberately NOT `save()`: that re-stamps `sentAt`, which would hand the
   * user a fresh 60s resend wait for a code that was mailed minutes ago. This
   * clock belongs to the send, not to the navigation.
   */
  function resume(session: OtpSession): OtpSession {
    return session.active ? session : write({ ...session, active: true })
  }

  /** Whole seconds left on the resend cooldown for `session`; 0 once resend is allowed. */
  function resendRemainingSeconds(session: OtpSession): number {
    const remaining = OTP_RESEND_COOLDOWN_MS - (Date.now() - session.sentAt)
    return remaining > 0 ? Math.ceil(remaining / 1000) : 0
  }

  /** True when `email` is the address the outstanding code was mailed to. */
  function matches(session: OtpSession, email: string): boolean {
    return session.email === normalizeOtpEmail(email)
  }

  return { read, save, dismiss, resume, clear, resendRemainingSeconds, matches }
}
